import { W, H, DOOR_W, PHYS, PLATFORMS, DOORS, SPAWN, STEP, KEY } from "./world";
import { generateMaze, LEVELS, DOORS_PER_ROOM } from "./maze";
import { spawnPuff, updatePuffs } from "./gas";
import { STRIKE } from "./escape";
import { render } from "./render";
import { sfx } from "./sound";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const IN_FRAMES = 26;
const MARK_TYPES = 5;
const OUT_FRAMES = 22;
const ESCAPE_FRAMES = { strike: 330, run: 260 };
const GAS_FRAMES = 210;

const makeDust = () =>
  Array.from({ length: 46 }, () => ({
    x: Math.random() * W,
    y: Math.random() * H,
    vx: (Math.random() - 0.5) * 0.15,
    vy: -0.05 - Math.random() * 0.12,
    s: Math.random() < 0.3 ? 2 : 1,
    a: 0.05 + Math.random() * 0.18,
  }));

export class Game {
  constructor(cb) {
    this.cb = cb;
    this.keys = {};
    this.tick = 0;
    this.paused = true;
    this.dust = makeDust();
    this.reset();
  }

  reset(level = this.level || "normale") {
    this.level = level;
    this.maze = generateMaze(level);
    this.room = 0;
    this.doors = 0;
    this.time = 0;
    this.state = "playing";
    this.trans = null;
    this.escape = null;
    this.gas = null;
    this.puffs = [];
    this.limit = LEVELS[level].time;
    this.flash = null;
    this.shake = 0;
    this.fade = 1;
    this.jumpBuffer = 0;
    this.coyote = 0;
    this.dropTimer = 0;
    this.upQueued = false;
    this.markQueued = false;
    this.marks = {};
    this.dipT = 0;
    this.allDoors = !!LEVELS[level].allDoors;
    this.hasKey = false;
    this.crossed = new Set();
    this.tinkleT = 120;
    this.totalDoors = LEVELS[level].rooms * DOORS_PER_ROOM;
    sfx.ambience(true);
    sfx.bgm(true);
    this.spawn();
    const self = this;
    window.__labirinto = {
      get room() { return self.room; },
      get maze() { return self.maze; },
      get player() { return self.p; },
      get marks() { return self.marks; },
      get hasKey() { return self.hasKey; },
      get crossed() { return self.crossed.size; },
      get totalDoors() { return self.totalDoors; },
    };
  }

  spawn(door) {
    const at = door === undefined ? SPAWN : { x: DOORS[door].x + DOOR_W / 2 - 12, y: DOORS[door].y - 34 };
    this.p = { x: at.x, y: at.y, w: 24, h: 34, vx: 0, vy: 0, onGround: true, face: 1, squash: 0 };
    this.playerAlpha = 1;
  }

  press(key, down) {
    if (down && !this.keys[key]) {
      if (key === "jump") this.jumpBuffer = 7;
      if (key === "up") this.upQueued = true;
      if (key === "mark") this.markQueued = true;
      if (key === "down") this.tryDrop();
    }
    this.keys[key] = down;
  }

  stats() {
    return { time: Math.round(this.time), doors: this.doors, left: Math.max(0, Math.round(this.limit - this.time)), hasKey: this.hasKey };
  }

  nearDoor() {
    const p = this.p;
    if (!p.onGround) return -1;
    const cx = p.x + p.w / 2;
    return DOORS.findIndex((d) => cx > d.x + 2 && cx < d.x + DOOR_W - 2 && Math.abs(p.y + p.h - d.y) < 2);
  }

  tryDrop() {
    const p = this.p;
    const on = PLATFORMS.find((pl) => pl.oneWay && Math.abs(p.y + p.h - pl.y) < 1 && p.x + p.w > pl.x && p.x < pl.x + pl.w);
    if (p.onGround && on) {
      this.dropTimer = 10;
      p.y += 2;
      p.onGround = false;
    }
  }

  update() {
    this.tick++;
    for (const d of this.dust) {
      d.x = (d.x + d.vx + W) % W;
      d.y = d.y + d.vy < 0 ? H : d.y + d.vy;
    }
    this.shake *= 0.9;
    if (this.dipT > 0) this.dipT--;
    else if (Math.random() < 0.004) {
      this.dipT = 4 + Math.floor(Math.random() * 12);
      sfx.flicker(this.dipT);
    }
    if (this.flash) this.flash.a *= this.flash.decay;
    if (!this.trans && this.fade > 0) this.fade = Math.max(0, this.fade - 0.06);
    if (this.paused) return;
    this.puffs = updatePuffs(this.puffs);
    if (this.state === "over") return this.updateEnd();
    this.time += STEP;
    const left = this.limit - this.time;
    if (left <= 0) return this.startGas();
    if (left < 30000 && Math.random() < 0.04) spawnPuff(this.puffs, 0.25);
    if (this.tick % 6 === 0) this.cb.onStats(this.stats());
    if (this.trans) return this.updateTransition();
    this.physics();
    this.checkKey();
    if (this.upQueued) {
      this.upQueued = false;
      const i = this.nearDoor();
      if (i >= 0) this.enterDoor(i);
    }
    if (this.markQueued) {
      this.markQueued = false;
      const i = this.nearDoor();
      if (i >= 0) {
        const k = `${this.room}-${i}`;
        this.marks[k] = ((this.marks[k] || 0) + 1) % MARK_TYPES;
        sfx.chalk();
      }
    }
  }

  physics() {
    const p = this.p;
    const k = this.keys;
    const dir = (k.right ? 1 : 0) - (k.left ? 1 : 0);
    if (dir) {
      p.vx = clamp(p.vx + dir * PHYS.accel, -PHYS.speed, PHYS.speed);
      p.face = dir;
    } else {
      p.vx *= PHYS.friction;
      if (Math.abs(p.vx) < 0.05) p.vx = 0;
    }
    if (this.jumpBuffer > 0) this.jumpBuffer--;
    if (this.dropTimer > 0) this.dropTimer--;
    this.coyote = p.onGround ? 6 : Math.max(0, this.coyote - 1);
    if (this.jumpBuffer > 0 && this.coyote > 0) {
      p.vy = PHYS.jump;
      p.onGround = false;
      this.coyote = 0;
      this.jumpBuffer = 0;
      p.squash = -0.22;
    }
    if (!k.jump && p.vy < -4) p.vy = -4;
    p.vy = Math.min(p.vy + PHYS.gravity, PHYS.maxFall);
    p.x = clamp(p.x + p.vx, 0, W - p.w);
    const prevBottom = p.y + p.h;
    p.y += p.vy;
    if (p.y < 20) {
      p.y = 20;
      p.vy = 0;
    }
    const wasGround = p.onGround;
    p.onGround = false;
    if (p.vy >= 0) {
      for (const pl of PLATFORMS) {
        if (pl.oneWay && this.dropTimer > 0) continue;
        const overlap = p.x + p.w > pl.x + 2 && p.x < pl.x + pl.w - 2;
        if (overlap && prevBottom <= pl.y + 0.01 && p.y + p.h >= pl.y) {
          p.y = pl.y - p.h;
          p.vy = 0;
          p.onGround = true;
          if (!wasGround) {
            p.squash = 0.28;
            sfx.land();
          }
          break;
        }
      }
    }
    p.squash *= 0.8;
    if (p.onGround && Math.abs(p.vx) > 1) {
      if (--this.stepTimer <= 0) {
        sfx.step();
        this.stepTimer = 15;
      }
    } else this.stepTimer = 4;
    p.groundY = this.groundBelow();
  }

  groundBelow() {
    const p = this.p;
    let best = H;
    for (const pl of PLATFORMS) {
      if (p.x + p.w > pl.x && p.x < pl.x + pl.w && pl.y >= p.y + p.h - 1 && pl.y < best) best = pl.y;
    }
    return best;
  }

  checkKey() {
    const p = this.p;
    if (this.hasKey || this.room !== this.maze.key) return;
    if (p.x + p.w > KEY.x && p.x < KEY.x + KEY.w && p.y < KEY.y + KEY.h && p.y + p.h > KEY.y) {
      this.hasKey = true;
      this.flash = { color: "rgba(214,196,150,A)", a: 0.35, decay: 0.9 };
      sfx.key();
      this.cb.onStats(this.stats());
      return;
    }
    if (--this.tinkleT <= 0) {
      this.tinkleT = 240 + Math.floor(Math.random() * 300);
      const dx = KEY.x + KEY.w / 2 - (p.x + p.w / 2);
      const near = Math.max(0, 1 - Math.hypot(dx, p.y - KEY.y) / 700);
      sfx.keyTinkle(near, dx / 480);
    }
  }

  enterDoor(i) {
    let link = this.maze.links[this.room][i];
    if (this.allDoors && this.hasKey) {
      const ends = [`${this.room}-${i}`, `${link.to}-${link.door}`];
      const after = this.crossed.size + new Set(ends.filter((k) => !this.crossed.has(k))).size;
      if (after >= this.totalDoors) link = { type: "exit" };
    }
    this.trans = { door: i, link, phase: "in", t: 0, fromX: this.p.x };
    this.p.vx = 0;
    sfx.creak();
  }

  updateTransition() {
    const tr = this.trans;
    const p = this.p;
    tr.t++;
    if (tr.phase === "in") {
      const k = Math.min(1, tr.t / IN_FRAMES);
      p.x = tr.fromX + (DOORS[tr.door].x + DOOR_W / 2 - p.w / 2 - tr.fromX) * Math.min(1, k * 2);
      this.playerAlpha = 1 - k;
      if (tr.link.type === "room") this.fade = Math.max(0, (tr.t - 10) / (IN_FRAMES - 10));
      if (tr.t >= IN_FRAMES) this.resolveDoor(tr);
    } else if (tr.phase === "out") {
      this.fade = 1 - tr.t / OUT_FRAMES;
      this.playerAlpha = Math.min(1, tr.t / OUT_FRAMES);
      if (tr.t >= OUT_FRAMES) {
        this.fade = 0;
        this.trans = null;
      }
    }
  }

  resolveDoor(tr) {
    this.doors++;
    this.cb.onStats(this.stats());
    if (tr.link.type === "room") {
      this.crossed.add(`${this.room}-${tr.door}`);
      this.crossed.add(`${tr.link.to}-${tr.link.door}`);
      this.room = tr.link.to;
      this.tinkleT = 90 + Math.floor(Math.random() * 150);
      this.spawn(tr.link.door);
      this.playerAlpha = 0;
      this.upQueued = false;
      sfx.close();
      this.fade = 1;
      this.trans = { ...tr, phase: "out", t: 0 };
      return;
    }
    this.state = "over";
    const strike = this.allDoors;
    this.endTimer = strike ? ESCAPE_FRAMES.strike : ESCAPE_FRAMES.run;
    tr.phase = "end";
    this.escape = { t: 0, seed: Math.floor(Math.random() * 1000), strike };
    this.fade = 0;
    this.flash = { color: "rgba(230,215,170,A)", a: 1, decay: 0.95 };
    sfx.escape();
  }

  startGas() {
    this.time = this.limit;
    this.cb.onStats(this.stats());
    this.state = "over";
    this.trans = null;
    this.fade = 0;
    this.playerAlpha = 1;
    this.gas = { t: 0, ground: this.groundBelow() };
    this.endTimer = GAS_FRAMES;
    sfx.gas();
  }

  updateEnd() {
    if (this.escape) {
      const e = this.escape;
      e.t++;
      if (e.t > 30 && e.t < (e.strike ? STRIKE : 200) && e.t % 13 === 0) sfx.step();
      if (e.strike && e.t === STRIKE) sfx.thunder();
      if (e.strike && e.t === STRIKE + 67) sfx.land();
    } else {
      const g = this.gas;
      g.t++;
      if (g.t < 150) for (let i = 0; i < 3; i++) spawnPuff(this.puffs, 1);
      this.p.y = Math.min(this.p.y + 5, g.ground - this.p.h);
      if (g.t === 92) sfx.land();
    }
    if (this.endTimer > 0 && --this.endTimer === 0) this.cb.onEnd(this.escape ? "exit" : "gas", this.stats());
  }

  render(ctx) {
    render(ctx, this);
  }
}
